import { observable, set, action, computed } from 'mobx';

import AccessLevelService, {
  free,
  gold,
  goldFree,
  goldPromo,
  guest,
  siteModerator,
  unverified,
  userModerator,
} from 'services/AccessLevelService';
import fanClubService from 'services/fanClub';
import localStorageService from 'services/LocalStorageService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  FANCLUB_USER_LOGGED_IN,
  GET_USER_CONSENT_STATUS,
  GET_USER_CONSENT_STATUS_ERROR,
  GET_USER_CONSENT_STATUS_RESPONSE,
  GET_USER_DATA_RESPONSE,
  PROFILE_CONTROLLED_FEATURES,
  SEND_USER_FEEDBACK_MODAL,
  SET_RESEND_EMAIL_RESPONSE,
  SET_USER_GENDER_PREFERENCE,
  TOGGLE_VERSION_UPDATE,
  USER_JOINED_FANCLUB_NOTIFICATION,
  USER_LOGGED_OUT_PENDING,
} from 'services/MessagingService/EventNames';
import trackingService from 'services/UITrackingService';
import UserService from 'services/UserService';
import { setPrivateShowAction } from 'webchat/webchatCalls';
import APIStates from 'enum/APIStates';
import { AVAILABLE_COUNTRIES } from 'enum/availableCountries';
import { AVAILABLE_LANGUAGES } from 'enum/availableLanguages';
import ControlledFeatures from 'enum/controlledFeatures';
import { USER_MODALS_GROUP } from 'enum/helpers';
import { CASHOUT_STATUS } from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';

import { ConsentData, User, UserConsent, UserModalData, WebRTCSource } from './UserStore.types';


class UserStore {

  messagingService;
  friends: Array<any>;
  favorites: Array<any>;

  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(GET_USER_DATA_RESPONSE, this._setUser);
    this.messagingService.on(SET_USER_GENDER_PREFERENCE, this._handleUserGenderPreferenceChange);
    this.messagingService.on(GET_USER_CONSENT_STATUS, this._consentApiRequested);
    this.messagingService.on(GET_USER_CONSENT_STATUS_RESPONSE, this._handleConsentApiResponse);
    this.messagingService.on(GET_USER_CONSENT_STATUS_ERROR, this._handleConsentApiError);
    this.messagingService.on(SET_RESEND_EMAIL_RESPONSE, this.handleResendEmailResponse);
    this.messagingService.on(SEND_USER_FEEDBACK_MODAL, this.handleSendFeedbackModal);
    this.messagingService.on(TOGGLE_VERSION_UPDATE, this.toggleVersionUpdate);
    this.messagingService.on(PROFILE_CONTROLLED_FEATURES, this.handleProfileCF);
    this.messagingService.on(USER_LOGGED_OUT_PENDING, this.setLogoutResponsePending);
    this.messagingService.on(FANCLUB_USER_LOGGED_IN, this.setUserHasLoggedIn);
    this.messagingService.on(USER_JOINED_FANCLUB_NOTIFICATION, this.setUserJoinedFanclub);
  }

  @observable user: User = null;
  @observable userConsent: UserConsent = {
    apiState: APIStates.INITIAL,
    data: null,
  };

  @observable isLogoutResponsePending = false;
  @observable showSFWAgeVerificationModal = false;
  @observable resendEmailResponse = false;
  @observable resendEmailValue: string = null;
  @observable isNewVersion = false;
  @observable c2cAudioMuted = false;
  @observable c2cVideoMuted = false;
  @observable WebRTCSource: WebRTCSource | null = null;
  @observable c2cViewerStreamStarted = false;
  @observable userHasLoggedIn = false;
  @observable hasJoinedFanclub = false;
  @observable isEventCarouselOpen = true;

  @action setC2cViewerStreamStarted = (value: boolean): void => {
    this.c2cViewerStreamStarted = value;
  };

  @action setWebRTCSource = (output: {
    protocol: string,
    payload: any,
    uri: string,
    provider: string,
  }): void => {
    if (!output) {
      this.WebRTCSource = null;

      return;
    }

    const { payload, uri } = output;

    const source: WebRTCSource = {
      type: 'WebRTC',
      uri,
    };

    if (output.provider === 'WOWZA') {
      const { accessHash, applicationName, streamName, username, playToken: webrtcPlayToken } = payload;

      source.wowza = {
        applicationName,
        streamName,
        userData: {
          username,
          streamKey: streamName,
          accessHash,
          webrtcPlayToken,
        },
      };
    }

    this.WebRTCSource = source;
  };

  @action setC2CAudioMuted = (muted: boolean): void => {
    this.c2cAudioMuted = muted;
  };

  @action setC2CVideoMuted = (muted: boolean): void => {
    this.c2cVideoMuted = muted;
  };

  @action setShowEventCarousel = (isOpen: boolean): void => {
    this.isEventCarouselOpen = isOpen;
  };

  @observable profileControlledFeatures: Array<string> = [];

  @action _setUser = (user: User): void => {
    this.user = user;
    // needed for purposes of ab tests, please don't remove
    // if value is undefined then user is not logged in
    window.hasPurchases = user.paymentInfo ? user.paymentInfo.hasPurchases : undefined;

    trackingService.emit('setUserInfo', user);
  };

  @action setLogoutResponsePending = (isLogoutResponsePending: boolean): void => {
    this.isLogoutResponsePending = isLogoutResponsePending;
  };

  @action setUserHasLoggedIn = (): void => {
    this.userHasLoggedIn = true;
  };

  @action setUserJoinedFanclub = (): void => {
    fanClubService.resetToken();
    this.hasJoinedFanclub = true;
  };

  @action toggleVersionUpdate = (isNewVersion: boolean): void => {
    this.isNewVersion = isNewVersion;
  };

  @action _handleUserGenderPreferenceChange = (genderPreference: string): void => {
    window.dataLayer.push({ Sexuality: `${genderPreference}` });
    set(this.user, { genderPreference });
  };

  @action _handleConsentApiResponse = (data: ConsentData): void => {
    set(this.userConsent, {
      apiState: APIStates.LOADED,
      data,
    });
  };

  @action _handleConsentApiError = (): void => {
    set(this.userConsent, { apiState: APIStates.ERROR });
  };

  @action _consentApiRequested = (): void => {
    set(this.userConsent, { apiState: APIStates.PENDING });
  };

  @action resendEmail = (email: string): void => {
    UserService.resendEmail(email);
  };

  @action handleResendEmailResponse = (resendEmailResponse: boolean): void => {
    this.resendEmailResponse = resendEmailResponse;
  };

  @action handleResendEmailValue = (resendEmailValue: string): void => {
    this.resendEmailValue = resendEmailValue;
  };

  @action handleProfileCF = (data: Array<string>): void => {
    if (Array.isArray(data)) {
      this.profileControlledFeatures = data;
    }
  };

  addNewUserModal = (data: UserModalData): void => {
    UserService.addNewUserModal(data);
  };

  updateC2CSettingsInFirebase = async(username: string, accessHash: string, roomname: string) => {
    await setPrivateShowAction({
      action: 'updatePSViewerSettings',
      username: username.toLowerCase(),
      accessHash,
      roomname,
      c2cViewerAudioMuted: this.c2cAudioMuted,
      c2cViewerVideoMuted: this.c2cVideoMuted,
    });
  };

  handleSendFeedbackModal = (): void => {
    const data = {
      username: this.user && this.user.username,
      modalType: USER_MODALS_GROUP.PROFILE_FEEDBACK,
      action: 'CLOSE',
    };
    this.addNewUserModal(data);
  };

  sendBroadcastTutorialInteraction = (actionType: string): void => {
    UserService.addNewUserModal({
      username: this.user.username,
      modalType: USER_MODALS_GROUP.BWR_TUTORIAL,
      action: actionType,
    });
  };

  hasCF = (cf: string): boolean => computed(() => {
    let localStorageCF = undefined;

    try {
      localStorageCF = JSON.parse(localStorageService.get(`FC_${cf}`)) || undefined;
    } catch (err) {
      // Ignore and move forward
    }

    if (localStorageCF !== undefined) {
      return localStorageCF;
    }

    const forcedCF = window.forceUserControlledFeatures || [];
    if (forcedCF.includes(cf)) {
      return true;
    }

    return this.user.controlledFeatures.includes(cf) || this.profileControlledFeatures.includes(cf);
  }).get();

  hasProfileCF = (cf: string): boolean => computed(() => this.profileControlledFeatures.includes(cf)).get();

  hasUserModal = (userModalType: string): boolean =>
    computed(() => this.user?.userModals?.some((item) => item.modalType === userModalType)).get();

  hasSubmittedModal = (userModalType: string): boolean =>
    computed(() => this.user?.userModals?.filter((item) => item.modalType === userModalType)
      .some((item) => item.action === 'SUBMIT')
    ).get();

  @computed get isLoggedIn(): boolean {
    return this.user.username !== null;
  }

  @computed get isUserConsentRequestDone(): boolean {
    return this.userConsent.apiState === APIStates.LOADED || this.userConsent.apiState === APIStates.ERROR;
  }

  @computed get hasAdminPrivileges(): boolean {
    return AccessLevelService.isMin(siteModerator, this.user.accessLevel);
  }

  @computed get isGuestUser(): boolean {
    return AccessLevelService.is(unverified, this.user.accessLevel);
  }

  @computed get isGuestBilling(): boolean {
    return AccessLevelService.is(guest, this.user.accessLevel);
  }

  @computed get isBasicUser(): boolean {
    return AccessLevelService.is(free, this.user.accessLevel);
  }

  @computed get isMinBasicUser(): boolean {
    return AccessLevelService.isMin(free, this.user.accessLevel);
  }

  @computed get isGoldUser(): boolean {
    return (
      AccessLevelService.is(gold, this.user.accessLevel) ||
      AccessLevelService.is(goldFree, this.user.accessLevel) ||
      AccessLevelService.is(goldPromo, this.user.accessLevel)
    );
  }

  @computed get isMinUserModerator(): boolean {
    return AccessLevelService.isMin(userModerator, this.user.accessLevel);
  }

  @computed get userBroadcastApproved(): boolean {
    return (
      this.user.paypalStatus &&
      (this.user.paypalStatus.paypalStatus === CASHOUT_STATUS.APPROVED_2257 ||
        this.user.paypalStatus.paypalStatus === CASHOUT_STATUS.BROADCAST_APPROVED ||
        this.user.paypalStatus.paypalStatus === CASHOUT_STATUS.CASH_OUT_APPROVED)
    );
  }

  @computed get userCashoutApproved(): boolean {
    return (
      this.user.paypalStatus &&
      this.user.paypalStatus.paypalStatus === CASHOUT_STATUS.CASH_OUT_APPROVED &&
      !this.user.paypalStatus.cashOutHold
    );
  }

  @computed get noPaypalStatus(): boolean {
    return this.user.paypalStatus && this.user.paypalStatus.paypalStatus === CASHOUT_STATUS.NONE;
  }

  @computed get cashoutHold(): boolean {
    return this.user.paypalStatus && this.user.paypalStatus.cashOutHold;
  }

  @computed get needsId(): boolean {
    return this.user.paypalStatus && this.user.paypalStatus.needsId;
  }

  @computed get defaultPage(): string {
    const { username, realCountry } = this.user;
    // eslint-disable-next-line no-warning-comments
    /*
    TODO add 'language' field to the user (from backend) - based on user
    conversation history plus other sources (browser language, location, etc)
    */
    const language = realCountry;
    const countryName = AVAILABLE_COUNTRIES.filter((e) => e.code === realCountry)[0]?.name;
    const languageName = AVAILABLE_LANGUAGES.filter((e) => e.code === language)[0]?.name;
    const defaultToSameCountry = this.hasCF(ControlledFeatures.DIRECTORY_DEFAULT_TO_SAME_COUNTRY);
    const defaultToSameLanguage = this.hasCF(ControlledFeatures.DIRECTORY_DEFAULT_TO_SAME_LANGUAGE);
    const defaultToGenderAll = this.hasCF(ControlledFeatures.DIRECTORY_DEFAULT_GENDER_ALL);

    let { root } = routeDefinitions;
    if (!username) {
      const parts = [];
      if (defaultToGenderAll) {
        parts.push('all');
      }
      if (defaultToSameLanguage && languageName) {
        parts.push(`${languageName.toLowerCase()}`);
      }
      if (defaultToSameCountry && countryName) {
        parts.push(`${countryName.toLowerCase()}`);
      }
      root = `/${parts.join('/')}`;
    }

    return root;
  }
}

export default new UserStore();
